import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Attach token from localStorage as Authorization header fallback
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("gv_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Force-logout on inactive-account responses. When a super admin deactivates
// a user mid-session, every subsequent API call returns 401 "Account is
// inactive — contact admin"; this interceptor clears the token and routes
// the browser to /login with a flash message so the user understands why.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const detail = err?.response?.data?.detail;
    if (
      err?.response?.status === 401 &&
      typeof detail === "string" &&
      detail.toLowerCase().includes("inactive")
    ) {
      try { localStorage.removeItem("gv_token"); } catch (e) {
        // SecurityError in privacy-mode iOS Safari — already logged out, safe to ignore.
        console.warn("Could not clear gv_token from localStorage:", e);
      }
      // Avoid redirect loops if we're already on /login.
      if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
        window.location.replace("/login?reason=inactive");
      }
    }
    return Promise.reject(err);
  },
);

export default api;

// ---- Mobile-safe file download ----
// iOS Safari and most mobile browsers either ignore the `download` attribute
// on programmatically-created anchors OR refuse to trigger downloads on
// detached <a> elements. This helper:
//   1. Authenticates the fetch with the JWT
//   2. Builds a Blob URL
//   3. On iOS, opens it in a new tab (user saves from there)
//   4. On everything else, appends the anchor to the DOM before clicking,
//      then removes it. Falls back to window.open if click() didn't fire.
function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && "ontouchend" in document);
}

export async function downloadAuthedFile(path, filename, { method = "GET", body, errorMessage = "Download failed" } = {}) {
  const token = localStorage.getItem("gv_token");
  const res = await fetch(`${API}${path}`, {
    method,
    headers: {
      Authorization: token ? `Bearer ${token}` : "",
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let detail = errorMessage;
    try { detail = (await res.json())?.detail || detail; } catch (e) {
      // Non-JSON error body (e.g. proxy HTML 502) — keep the default messsage.
      console.warn("Could not parse error response as JSON:", e);
    }
    throw new Error(typeof detail === "string" ? detail : errorMessage);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);

  if (isIOS()) {
    // iOS Safari: open in a new tab. The PDF renders inline and the user uses
    // the browser's share-sheet to save. Programmatic download is unreliable.
    const w = window.open(url, "_blank");
    if (!w) {
      // Pop-up blocked — fall back to navigating the current tab (still works).
      window.location.href = url;
    }
    // Keep the blob URL alive long enough for the new tab to load.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
    return;
  }

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  // CRITICAL: must be in DOM on Mobile Safari / older Android Chrome
  document.body.appendChild(a);
  a.click();
  // Tear down on next tick so the click handler completes first
  setTimeout(() => {
    a.remove();
    URL.revokeObjectURL(url);
  }, 0);
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function formatCurrency(n) {
  if (n == null || isNaN(n)) return "Rp 0";
  return "Rp " + new Intl.NumberFormat("id-ID", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

export function formatCurrencyDecimal(n) {
  if (n == null || isNaN(n)) return "Rp 0";
  return "Rp " + new Intl.NumberFormat("id-ID", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
}

/** Resolve final unit label for a product or sale row.
 * If unit === "custom" and custom_unit is set, returns the custom string.
 */
export function unitLabel(unit, customUnit) {
  if (!unit) return "pcs";
  if (unit === "custom") return (customUnit || "").trim() || "unit";
  return unit;
}

/** Format a decimal quantity with its unit. Does NOT round - always shows up to 2 decimals.
 * Uses dot decimal separator (matching spec examples: 1002.20 ct, 0.85 g).
 * Examples: formatQty(1002.20, "ct") => "1,002.20 ct"; formatQty(2, "pcs") => "2 pcs"
 */
export function formatQty(qty, unit, customUnit) {
  if (qty == null || isNaN(qty)) return "0 " + unitLabel(unit, customUnit);
  const n = Number(qty);
  const isWhole = Math.abs(n - Math.round(n)) < 1e-9;
  const formatted = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: isWhole ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(n);
  return `${formatted} ${unitLabel(unit, customUnit)}`;
}

/** Weight conversion helpers.
 * 1 ct = 0.2 g; 1 g = 5 ct; 1 kg = 1000 g
 */
export const UNIT_OPTIONS = [
  { value: "ct", label: "Carat (ct)" },
  { value: "g", label: "Gram (g)" },
  { value: "kg", label: "Kilogram (kg)" },
  { value: "pcs", label: "Piece (pcs)" },
  { value: "custom", label: "Custom unit" },
];

/** Convert a value between weight units. Returns null if either side isn't a weight unit. */
export function convertWeight(value, from, to) {
  if (value == null || isNaN(value)) return null;
  const FROM_G = { ct: 0.2, g: 1, kg: 1000 };
  if (!(from in FROM_G) || !(to in FROM_G)) return null;
  const grams = Number(value) * FROM_G[from];
  return grams / FROM_G[to];
}


/** Normalise a phone number for use in https://wa.me/<phone>.
 * Strips spaces/dashes/brackets/plus, converts leading 0 → 62 (Indonesia).
 * Returns "" if not a usable number.
 */
export function waPhone(raw) {
  if (!raw) return "";
  let digits = String(raw).replace(/[^\d]/g, "");
  if (!digits) return "";
  if (digits.startsWith("0")) digits = "62" + digits.slice(1);
  return digits;
}

/** Build a WhatsApp deep-link for a sale invoice. */
export function whatsappInvoiceLink(sale, { businessName = "Opik Jewels" } = {}) {
  const phone = waPhone(sale.customer_phone);
  if (!phone) return "";
  const isMulti = sale.is_multi && Array.isArray(sale.items) && sale.items.length > 0;
  const lines = [
    `Hi ${sale.customer_name || "there"},`,
    "",
    `Thank you for your purchase at ${businessName}.`,
    "",
    `Invoice: ${sale.invoice_number}`,
  ];
  if (isMulti) {
    lines.push(`Items (${sale.items.length}):`);
    sale.items.forEach((it, i) => {
      const q = formatQty(it.quantity, it.product_unit, it.product_custom_unit);
      lines.push(`  ${i + 1}. ${it.product_name} — ${q} — ${formatCurrency(it.line_subtotal)}`);
    });
    lines.push("");
    lines.push(`Subtotal: ${formatCurrency(sale.subtotal_amount || 0)}`);
    if ((sale.discount || 0) > 0) lines.push(`Discount: -${formatCurrency(sale.discount)}`);
    if ((sale.tax_pct || 0) > 0) lines.push(`Tax (${sale.tax_pct}%): ${formatCurrency(sale.tax_amount || 0)}`);
    lines.push(`*Total: ${formatCurrency(sale.total)}*`);
  } else {
    lines.push(`Item: ${sale.product_name}`);
    lines.push(`Quantity: ${formatQty(sale.quantity, sale.product_unit, sale.product_custom_unit)}`);
    lines.push(`Total: ${formatCurrency(sale.total)}`);
  }
  lines.push(`Payment: ${sale.payment_method || "Wire"}`);
  lines.push("");
  lines.push("Your official invoice PDF will follow shortly.");
  lines.push(`— ${businessName}`);
  const text = encodeURIComponent(lines.join("\n"));
  return `https://wa.me/${phone}?text=${text}`;
}
